/**
 * Runtime Config Add Forms
 *
 * Freeform "Add file" / "Add env var" forms for RuntimeConfigDialog.
 * Extracted to keep the dialog under the 200-line component cap.
 */

import { useState, type ReactElement, type FormEvent } from 'react';

const ENV_NAME_RE = /^[A-Z_][A-Z0-9_]*$/;

interface AddFileFormProps {
  onSubmit: (name: string, payload: { value: string; target_path: string; file_mode: string }) => Promise<void>;
  isSaving: boolean;
}

export function AddFileForm({ onSubmit, isSaving }: AddFileFormProps): ReactElement {
  const [name, setName] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [fileMode, setFileMode] = useState('0600');
  const [value, setValue] = useState('');

  const canSubmit = name.trim().length > 0 && targetPath.trim().length > 0 && value.length > 0;

  // Only clear fields on success. On failure (e.g. a 422 on a bad
  // target_path/file_mode or a size cap) the user's input — including a
  // freshly pasted kubeconfig — must survive so they can fix one field and
  // resubmit. The error itself is surfaced by the dialog-level banner.
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    void onSubmit(name.trim(), {
      value,
      target_path: targetPath.trim(),
      file_mode: fileMode.trim() || '0600',
    }).then(() => {
      setName('');
      setTargetPath('');
      setFileMode('0600');
      setValue('');
    }).catch(() => {});
  };

  return (
    <form className="dialog__form runtime-config__add-form" onSubmit={handleSubmit}>
      <label className="dialog__field">
        Name (key)
        <input value={name} onChange={(e) => { setName(e.target.value); }} placeholder="e.g. custom-config" required />
      </label>
      <label className="dialog__field">
        Target path
        <input value={targetPath} onChange={(e) => { setTargetPath(e.target.value); }} placeholder="~/.config/tool/config.yaml" required />
      </label>
      <label className="dialog__field">
        Mode
        <input value={fileMode} onChange={(e) => { setFileMode(e.target.value); }} placeholder="0600" />
      </label>
      <label className="dialog__field">
        Content
        <textarea className="runtime-config__textarea" value={value} onChange={(e) => { setValue(e.target.value); }} rows={4} required />
      </label>
      <div className="dialog__actions">
        <button type="submit" className="dialog__btn dialog__btn--primary" disabled={!canSubmit || isSaving}>
          {isSaving ? 'Saving…' : 'Add file'}
        </button>
      </div>
    </form>
  );
}

interface AddEnvVarFormProps {
  onSubmit: (name: string, payload: { value: string; secret: boolean }) => Promise<void>;
  isSaving: boolean;
}

export function AddEnvVarForm({ onSubmit, isSaving }: AddEnvVarFormProps): ReactElement {
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [secret, setSecret] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const canSubmit = ENV_NAME_RE.test(name.trim()) && value.length > 0;

  // Only clear fields on success — a failed save must leave the user's
  // input intact so they can fix it and resubmit without retyping. The
  // error itself is surfaced by the dialog-level banner.
  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!ENV_NAME_RE.test(trimmed)) {
      setNameError('Name must match ^[A-Z_][A-Z0-9_]*$');
      return;
    }
    setNameError(null);
    void onSubmit(trimmed, { value, secret }).then(() => {
      setName('');
      setValue('');
      setSecret(false);
    }).catch(() => {});
  };

  return (
    <form className="dialog__form runtime-config__add-form" onSubmit={handleSubmit}>
      <label className="dialog__field">
        Name
        <input
          value={name}
          onChange={(e) => { setName(e.target.value); setNameError(null); }}
          placeholder="MY_ENV_VAR"
          required
        />
      </label>
      {nameError && <div className="dialog__error" role="alert">{nameError}</div>}
      <label className="dialog__field">
        Value
        <textarea className="runtime-config__textarea" value={value} onChange={(e) => { setValue(e.target.value); }} rows={2} required />
      </label>
      <label className="dialog__field dialog__field--checkbox">
        <input type="checkbox" checked={secret} onChange={(e) => { setSecret(e.target.checked); }} />
        Secret
      </label>
      <div className="dialog__actions">
        <button type="submit" className="dialog__btn dialog__btn--primary" disabled={!canSubmit || isSaving}>
          {isSaving ? 'Saving…' : 'Add env var'}
        </button>
      </div>
    </form>
  );
}

type AddFilePayload = { value: string; target_path: string; file_mode: string };
type AddEnvPayload = { value: string; secret: boolean };

interface RuntimeConfigAddControlsProps {
  isSaving: boolean;
  onAddFile: (name: string, payload: AddFilePayload) => Promise<void>;
  onAddEnv: (name: string, payload: AddEnvPayload) => Promise<void>;
}

/** The "+ Add file" / "+ Add env var" toggles and whichever form is open. */
export function RuntimeConfigAddControls({
  isSaving,
  onAddFile,
  onAddEnv,
}: RuntimeConfigAddControlsProps): ReactElement {
  const [showAddFile, setShowAddFile] = useState(false);
  const [showAddEnv, setShowAddEnv] = useState(false);

  // A form closes only once its save resolves; on failure the input survives.
  const addFile = async (name: string, payload: AddFilePayload): Promise<void> => {
    await onAddFile(name, payload);
    setShowAddFile(false);
  };
  const addEnv = async (name: string, payload: AddEnvPayload): Promise<void> => {
    await onAddEnv(name, payload);
    setShowAddEnv(false);
  };

  return (
    <>
      <div className="runtime-config__add-toggles">
        <button
          type="button"
          className="project-features__options-btn"
          onClick={() => { setShowAddFile((v) => !v); setShowAddEnv(false); }}
        >
          {showAddFile ? 'Cancel' : '+ Add file'}
        </button>
        <button
          type="button"
          className="project-features__options-btn"
          onClick={() => { setShowAddEnv((v) => !v); setShowAddFile(false); }}
        >
          {showAddEnv ? 'Cancel' : '+ Add env var'}
        </button>
      </div>
      {showAddFile && <AddFileForm isSaving={isSaving} onSubmit={addFile} />}
      {showAddEnv && <AddEnvVarForm isSaving={isSaving} onSubmit={addEnv} />}
    </>
  );
}
